<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAddOnsStore } from 'dashboard/stores/addOns';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const props = defineProps({
  product: { type: Object, required: true },
});

const { t } = useI18n();
const store = useAddOnsStore();
const uiFlags = computed(() => store.uiFlags);
const addOns = computed(() =>
  store.records.filter(a => a.product_id === props.product.id)
);

const recurringIntervalOptions = computed(() => [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
]);

const showFormModal = ref(false);
const showDeleteModal = ref(false);
const selectedAddOn = ref(null);

const emptyForm = () => ({
  name: '',
  description: '',
  price: '0',
  recurring: false,
  recurring_interval: 'monthly',
  gst_percent: '18',
  active: true,
});
const form = reactive(emptyForm());
const isFormInvalid = computed(() => !form.name.trim());

const openAdd = () => {
  selectedAddOn.value = null;
  Object.assign(form, emptyForm());
  showFormModal.value = true;
};

const openEdit = addOn => {
  selectedAddOn.value = addOn;
  Object.assign(form, {
    name: addOn.name,
    description: addOn.description || '',
    price: String(addOn.price),
    recurring: addOn.recurring,
    recurring_interval: addOn.recurring_interval || 'monthly',
    gst_percent: String(addOn.gst_percent),
    active: addOn.active,
  });
  showFormModal.value = true;
};

const closeFormModal = () => {
  showFormModal.value = false;
};

const submitForm = async () => {
  if (isFormInvalid.value) return;
  const payload = {
    ...form,
    product_id: props.product.id,
    recurring_interval: form.recurring ? form.recurring_interval : null,
  };
  try {
    if (selectedAddOn.value) {
      await store.update({ id: selectedAddOn.value.id, ...payload });
    } else {
      await store.create(payload);
    }
    useAlert(t('PRODUCTS_MGMT.ADD_ONS.SAVED'));
    closeFormModal();
  } catch (error) {
    useAlert(error?.message || t('PRODUCTS_MGMT.ADD_ONS.SAVE_ERROR'));
  }
};

const openDelete = addOn => {
  selectedAddOn.value = addOn;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  try {
    await store.delete(selectedAddOn.value.id);
    useAlert(t('PRODUCTS_MGMT.ADD_ONS.DELETED'));
  } catch (error) {
    useAlert(error?.message || t('PRODUCTS_MGMT.ADD_ONS.DELETE_ERROR'));
  } finally {
    showDeleteModal.value = false;
  }
};

watch(
  () => props.product?.id,
  productId => {
    if (productId) store.get({ productId });
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
  >
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('PRODUCTS_MGMT.ADD_ONS.TITLE') }}
        <span class="text-sm font-normal text-n-slate-11">{{
          t('PRODUCTS_MGMT.ADD_ONS.FOR_PRODUCT', { product: product.name })
        }}</span>
      </h3>
      <Button
        :label="t('PRODUCTS_MGMT.ADD_ONS.ADD')"
        size="sm"
        @click="openAdd"
      />
    </div>
    <BaseTable
      :headers="[
        t('PRODUCTS_MGMT.ADD_ONS.TITLE'),
        t('PRODUCTS_MGMT.ADD_ONS.PRICE'),
        t('PRODUCTS_MGMT.PLANS.GST_PERCENT'),
        '',
      ]"
      :items="addOns"
      :no-data-message="t('PRODUCTS_MGMT.ADD_ONS.NO_DATA')"
    >
      <template #row="{ items }">
        <BaseTableRow v-for="addOn in items" :key="addOn.id" :item="addOn">
          <template #default>
            <BaseTableCell>
              <span class="text-body-main text-n-slate-12">{{
                addOn.name
              }}</span>
            </BaseTableCell>
            <BaseTableCell>
              {{ addOn.price
              }}{{ addOn.recurring ? ` / ${addOn.recurring_interval}` : '' }}
            </BaseTableCell>
            <BaseTableCell>{{ addOn.gst_percent }}%</BaseTableCell>
            <BaseTableCell align="end">
              <div class="flex justify-end gap-2">
                <Button
                  icon="i-woot-edit-pen"
                  slate
                  sm
                  @click="openEdit(addOn)"
                />
                <Button icon="i-woot-bin" slate sm @click="openDelete(addOn)" />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <woot-modal v-model:show="showFormModal" :on-close="closeFormModal">
      <div class="flex flex-col w-full gap-4 p-6">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{
            selectedAddOn
              ? t('PRODUCTS_MGMT.ADD_ONS.EDIT')
              : t('PRODUCTS_MGMT.ADD_ONS.ADD')
          }}
        </h3>
        <Input
          v-model="form.name"
          :placeholder="t('PRODUCTS_MGMT.ADD_ONS.NAME_PLACEHOLDER')"
          autofocus
        />
        <TextArea
          v-model="form.description"
          :placeholder="t('PRODUCTS_MGMT.SECTORS.DESCRIPTION_PLACEHOLDER')"
        />
        <div class="grid grid-cols-2 gap-4">
          <Input
            v-model="form.price"
            type="number"
            :label="t('PRODUCTS_MGMT.ADD_ONS.PRICE')"
          />
          <Input
            v-model="form.gst_percent"
            type="number"
            :label="t('PRODUCTS_MGMT.PLANS.GST_PERCENT')"
          />
        </div>
        <div class="flex items-center gap-2">
          <Switch v-model="form.recurring" />
          <span class="text-sm text-n-slate-11">{{
            t('PRODUCTS_MGMT.ADD_ONS.RECURRING')
          }}</span>
        </div>
        <div v-if="form.recurring" class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">{{
            t('PRODUCTS_MGMT.ADD_ONS.RECURRING_INTERVAL')
          }}</span>
          <SelectInput
            v-model="form.recurring_interval"
            :options="recurringIntervalOptions"
          />
        </div>
        <div class="flex items-center gap-2">
          <Switch v-model="form.active" />
          <span class="text-sm text-n-slate-11">{{
            t('COMMON_MGMT.ACTIVE')
          }}</span>
        </div>
        <div class="flex justify-end gap-2">
          <Button
            :label="t('COMMON_MGMT.CANCEL')"
            variant="link"
            @click="closeFormModal"
          />
          <Button
            :label="t('COMMON_MGMT.SAVE')"
            :disabled="isFormInvalid"
            :is-loading="uiFlags.creatingItem || uiFlags.updatingItem"
            @click="submitForm"
          />
        </div>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="confirmDelete"
      :title="t('PRODUCTS_MGMT.ADD_ONS.DELETE_TITLE')"
      :message="t('PRODUCTS_MGMT.ADD_ONS.DELETE_MESSAGE')"
      :message-value="selectedAddOn?.name"
      :confirm-text="t('COMMON_MGMT.DELETE')"
      :reject-text="t('COMMON_MGMT.CANCEL')"
    />
  </div>
</template>
